import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, ShieldCheck, ChevronLeft, AlertTriangle } from 'lucide-react';
import NotificationBellButton from '../../components/NotificationBellButton';
import { useBackTarget } from '../../hooks/useBackTarget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const font = "'Inria Serif', serif";

export default function HostVatPage() {
  const goBack = useBackTarget();
  const [vatNumber, setVatNumber] = useState('');
  const [countryCode, setCountryCode] = useState('GR');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existingVat, setExistingVat] = useState(null);
  const [eligibility, setEligibility] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const fetchData = async () => {
      try {
        const [vatRes, eligRes] = await Promise.all([
          axios.get(`${API}/host/vat-settings`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/host/payout-eligibility`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        const vat = vatRes.data?.vat_settings;
        if (vat) {
          setExistingVat(vat);
          setVatNumber(vat.vat_number || '');
          setCountryCode(vat.country_code || 'GR');
        }
        setEligibility(eligRes.data);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSave = async () => {
    if (!vatNumber.trim()) {
      toast.error('Please enter your VAT/Tax ID number');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API}/host/vat-settings`, { vat_number: vatNumber, country_code: countryCode }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.data?.auto_payout_triggered) {
        toast.success(`VAT saved — your pending payout of €${Number(res.data.auto_payout_amount || 0).toFixed(2)} is on its way`);
      } else {
        toast.success('VAT/Tax ID saved successfully');
      }
      setExistingVat({ vat_number: vatNumber.trim().toUpperCase(), country_code: countryCode });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const hasPendingPayout = eligibility && !eligibility.has_vat && eligibility.current_month;

  return (
    <div className="app-shell" data-testid="host-vat-page" style={{ minHeight: '100vh', backgroundColor: 'var(--app-bg)', paddingBottom: 40 }}>
      <div style={{ padding: '16px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button onClick={goBack} data-testid="host-vat-page-back-button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--text)' }}>
          <ChevronLeft size={32} color="currentColor" strokeWidth={1.7} />
        </button>
        <NotificationBellButton buttonTestId="host-vat-page-bell-button" popupTestId="host-vat-page-notification-popup" />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 16px' }}>
        <img src="/allure-logo.png" alt="Allure" style={{ width: 150, height: 130, objectFit: 'contain', marginTop: 10 }} />
        <h1 style={{ fontFamily: font, fontSize: 24, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: '18px 0 8px', lineHeight: 1.15 }}>
          VAT / Tax ID
        </h1>

        <div style={{ width: '100%', maxWidth: 380, display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8 }}>

          {hasPendingPayout && (
            <div data-testid="host-vat-warning-banner" style={{
              display: 'flex', gap: 10, alignItems: 'flex-start', padding: '14px 16px',
              borderRadius: 16, backgroundColor: 'var(--surface)', border: '1.5px solid #F59E0B',
            }}>
              <AlertTriangle size={22} color="#F59E0B" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <p style={{ fontFamily: font, fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                  Payout on hold
                </p>
                <p style={{ fontFamily: font, fontSize: 13, color: 'var(--text-2)', margin: '4px 0 0', lineHeight: 1.4 }}>
                  Your earnings are ready but we need your VAT/Tax ID before we can send the money to your bank.
                </p>
              </div>
            </div>
          )}

          <div style={{
            padding: '18px 16px', borderRadius: 20,
            backgroundColor: 'var(--surface)', border: '1px solid var(--card-border)',
            boxShadow: 'var(--premium-shadow-main)',
          }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 16 }}>
              <ShieldCheck size={22} color="var(--accent-purple)" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <p style={{ fontFamily: font, fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                  Why do we need this?
                </p>
                <p style={{ fontFamily: font, fontSize: 13, color: 'var(--text-2)', margin: '6px 0 0', lineHeight: 1.5 }}>
                  EU regulations (DAC7) require platforms to collect your tax identification number before processing payouts.
                  This helps us generate your earnings statements and ensures everything is transparent with your local tax authority.
                  Your data is stored securely and only used for compliance purposes.
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <FileText size={22} color="var(--accent-purple)" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <p style={{ fontFamily: font, fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                  What do I enter?
                </p>
                <p style={{ fontFamily: font, fontSize: 13, color: 'var(--text-2)', margin: '6px 0 0', lineHeight: 1.5 }}>
                  Enter your VAT number (e.g. EL123456789 for Greece) or your national Tax Identification Number (TIN/AFM).
                  If you{"'"}re not VAT-registered, enter your personal tax ID instead.
                </p>
              </div>
            </div>
          </div>

          {loading ? (
            <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', textAlign: 'center' }}>Loading...</p>
          ) : (
            <div style={{
              padding: '18px 16px', borderRadius: 20,
              backgroundColor: 'var(--surface)', border: '1px solid var(--card-border)',
              boxShadow: 'var(--premium-shadow-main)',
              display: 'flex', flexDirection: 'column', gap: 14,
            }}>
              <div>
                <label htmlFor="vat-country" style={{ fontFamily: font, fontSize: 13, fontWeight: 700, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
                  Country
                </label>
                <select
                  id="vat-country"
                  data-testid="host-vat-country-select"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  style={{
                    width: '100%', padding: '12px 14px', borderRadius: 14,
                    border: '1px solid var(--card-border)', backgroundColor: 'var(--surface)',
                    fontFamily: font, fontSize: 15, color: 'var(--text)',
                  }}
                >
                  <option value="GR">Greece (GR)</option>
                  <option value="AT">Austria (AT)</option>
                  <option value="BE">Belgium (BE)</option>
                  <option value="BG">Bulgaria (BG)</option>
                  <option value="CY">Cyprus (CY)</option>
                  <option value="CZ">Czech Republic (CZ)</option>
                  <option value="DE">Germany (DE)</option>
                  <option value="DK">Denmark (DK)</option>
                  <option value="EE">Estonia (EE)</option>
                  <option value="ES">Spain (ES)</option>
                  <option value="FI">Finland (FI)</option>
                  <option value="FR">France (FR)</option>
                  <option value="HR">Croatia (HR)</option>
                  <option value="HU">Hungary (HU)</option>
                  <option value="IE">Ireland (IE)</option>
                  <option value="IT">Italy (IT)</option>
                  <option value="LT">Lithuania (LT)</option>
                  <option value="LU">Luxembourg (LU)</option>
                  <option value="LV">Latvia (LV)</option>
                  <option value="MT">Malta (MT)</option>
                  <option value="NL">Netherlands (NL)</option>
                  <option value="PL">Poland (PL)</option>
                  <option value="PT">Portugal (PT)</option>
                  <option value="RO">Romania (RO)</option>
                  <option value="SE">Sweden (SE)</option>
                  <option value="SI">Slovenia (SI)</option>
                  <option value="SK">Slovakia (SK)</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="vat-number" style={{ fontFamily: font, fontSize: 13, fontWeight: 700, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
                  VAT / Tax ID Number
                </label>
                <input
                  id="vat-number"
                  data-testid="host-vat-number-input"
                  type="text"
                  placeholder="e.g. EL123456789"
                  value={vatNumber}
                  onChange={(e) => setVatNumber(e.target.value)}
                  style={{
                    width: '100%', padding: '12px 14px', borderRadius: 14,
                    border: '1px solid var(--card-border)', backgroundColor: 'var(--surface)',
                    fontFamily: font, fontSize: 15, color: 'var(--text)',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {existingVat && (
                <div data-testid="host-vat-current-value" style={{
                  padding: '10px 14px', borderRadius: 12,
                  backgroundColor: 'rgba(74, 222, 128, 0.1)', border: '1px solid rgba(74, 222, 128, 0.3)',
                }}>
                  <p style={{ fontFamily: font, fontSize: 13, color: 'var(--text)', margin: 0 }}>
                    Current: <strong>{existingVat.vat_number}</strong> ({existingVat.country_code})
                  </p>
                </div>
              )}

              <button
                type="button"
                data-testid="host-vat-save-button"
                onClick={handleSave}
                disabled={saving || !vatNumber.trim()}
                style={{
                  width: '100%', padding: '14px 16px', borderRadius: 9999,
                  fontFamily: font, fontSize: 17, fontWeight: 700,
                  color: 'var(--accent-text-soft)', background: 'var(--surface)',
                  border: '1px solid var(--card-border)',
                  cursor: saving ? 'default' : 'pointer',
                  opacity: saving || !vatNumber.trim() ? 0.55 : 1,
                  boxShadow: 'var(--premium-shadow-main)',
                }}
              >
                {saving ? 'Saving...' : existingVat ? 'Update VAT / Tax ID' : 'Save VAT / Tax ID'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
